import React from 'react';
import { UsageSummary, Subscription } from 'api/rest/myaliro_billing';
import * as S from './UsageMetrics.styled';

interface UsageMetricsProps {
    usage: UsageSummary | null;
    subscription: Subscription | null;
}

interface TierInfo {
    name: string;
    range: string;
    feePerCase: number;
}

const TIER_STRUCTURE: TierInfo[] = [
    { name: 'Tier 1', range: '0-50 cases', feePerCase: 25 },
    { name: 'Tier 2', range: '51-100 cases', feePerCase: 20 },
    { name: 'Tier 3', range: '101+ cases', feePerCase: 15 },
];

const UsageMetrics: React.FC<UsageMetricsProps> = ({ usage, subscription }) => {
    if (!usage || !subscription) {
        return (
            <S.Container>
                <S.SectionTitle>Current Usage</S.SectionTitle>
                <S.EmptyState>
                    <S.EmptyStateText>
                        No usage data available for the current month.
                    </S.EmptyStateText>
                </S.EmptyState>
            </S.Container>
        );
    }

    // Special handling for SPECIAL_PARTNER package
    // TODO: Stripe subscriptions use product_id instead of package_type
    // Need to map product_id to package type or add metadata to response
    const isSpecialPartner = false;

    // Get current tier info
    const getCurrentTierInfo = (): {
        tierNumber: number;
        tierName: string;
        rangeEnd: number | null;
    } => {
        const caseCount = usage.case_count;

        if (caseCount <= 50) {
            return { tierNumber: 1, tierName: 'Tier 1', rangeEnd: 50 };
        } else if (caseCount <= 100) {
            return { tierNumber: 2, tierName: 'Tier 2', rangeEnd: 100 };
        } else {
            return { tierNumber: 3, tierName: 'Tier 3 (Top Tier)', rangeEnd: null };
        }
    };

    const currentTier = getCurrentTierInfo();

    // Calculate progress within current tier
    const getTierProgress = (): {
        casesInTier: number;
        casesToNext: number;
        progressPercent: number;
    } => {
        const caseCount = usage.case_count;

        if (caseCount <= 50) {
            return {
                casesInTier: caseCount,
                casesToNext: 50 - caseCount,
                progressPercent: (caseCount / 50) * 100,
            };
        } else if (caseCount <= 100) {
            const casesInTier = caseCount - 50;
            return {
                casesInTier,
                casesToNext: 100 - caseCount,
                progressPercent: (casesInTier / 50) * 100,
            };
        } else {
            return {
                casesInTier: caseCount - 100,
                casesToNext: 0,
                progressPercent: 100,
            };
        }
    };

    const tierProgress = getTierProgress();

    // Get billing month display
    const getBillingMonthDisplay = (): string => {
        if (!usage.billing_month) return 'Current Month';
        const date = new Date(usage.billing_month);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    };

    return (
        <S.Container>
            <S.SectionTitle>Current Usage</S.SectionTitle>

            <S.MetricsGrid>
                <S.MetricCard>
                    <S.MetricLabel>Billing Month</S.MetricLabel>
                    <S.MetricValue style={{ fontSize: '20px' }}>
                        {getBillingMonthDisplay()}
                    </S.MetricValue>
                </S.MetricCard>

                <S.MetricCard>
                    <S.MetricLabel>Case Count</S.MetricLabel>
                    <S.MetricValue>{usage.case_count}</S.MetricValue>
                    <S.MetricSubtext>cases submitted</S.MetricSubtext>
                </S.MetricCard>

                <S.MetricCard>
                    <S.MetricLabel>Current Tier</S.MetricLabel>
                    <S.MetricValue style={{ fontSize: '24px' }}>
                        {currentTier.tierName}
                    </S.MetricValue>
                    <S.MetricSubtext>{usage.current_tier}</S.MetricSubtext>
                </S.MetricCard>

                <S.MetricCard>
                    <S.MetricLabel>Fee Per Case</S.MetricLabel>
                    <S.MetricValue>${usage.fee_per_case}</S.MetricValue>
                    <S.MetricSubtext>per case</S.MetricSubtext>
                </S.MetricCard>
            </S.MetricsGrid>

            {!isSpecialPartner && (
                <>
                    <S.TierProgressSection>
                        <S.TierProgressLabel>Progress to Next Tier</S.TierProgressLabel>
                        <S.ProgressBarContainer>
                            <S.ProgressBar progress={tierProgress.progressPercent}>
                                {tierProgress.progressPercent > 15 && (
                                    <S.ProgressText>
                                        {Math.round(tierProgress.progressPercent)}%
                                    </S.ProgressText>
                                )}
                            </S.ProgressBar>
                        </S.ProgressBarContainer>
                        <S.TierInfoText>
                            {currentTier.rangeEnd ? (
                                <>
                                    {tierProgress.casesToNext} more{' '}
                                    {tierProgress.casesToNext === 1 ? 'case' : 'cases'} to reach
                                    Tier {currentTier.tierNumber + 1}
                                </>
                            ) : (
                                <>
                                    You've reached the top tier! All cases are billed at $15 per
                                    case.
                                </>
                            )}
                        </S.TierInfoText>
                    </S.TierProgressSection>

                    <S.TierProgressLabel>Tier Breakdown</S.TierProgressLabel>
                    <S.TierBreakdownTable>
                        <S.TableHeader>
                            <tr>
                                <S.TableHeaderCell>Tier</S.TableHeaderCell>
                                <S.TableHeaderCell>Case Range</S.TableHeaderCell>
                                <S.TableHeaderCell>Fee Per Case</S.TableHeaderCell>
                            </tr>
                        </S.TableHeader>
                        <tbody>
                            {TIER_STRUCTURE.map((tier, idx) => {
                                const isCurrentTier = idx + 1 === currentTier.tierNumber;
                                return (
                                    <S.TableRow key={idx} highlighted={isCurrentTier}>
                                        <S.TableCell>
                                            {isCurrentTier && <S.TierBadge>Current</S.TierBadge>}
                                            {!isCurrentTier && tier.name}
                                        </S.TableCell>
                                        <S.TableCell>{tier.range}</S.TableCell>
                                        <S.TableCell>
                                            <S.FeeAmount highlighted={isCurrentTier}>
                                                ${tier.feePerCase}
                                            </S.FeeAmount>
                                        </S.TableCell>
                                    </S.TableRow>
                                );
                            })}
                        </tbody>
                    </S.TierBreakdownTable>
                </>
            )}

            {isSpecialPartner && (
                <S.TierInfoText style={{ marginTop: '16px' }}>
                    As a Special Partner, all cases are billed at a flat rate of $15 per case
                    regardless of volume.
                </S.TierInfoText>
            )}
        </S.Container>
    );
};

export default UsageMetrics;
